/**
 * m-cart-upsell
 *
 * Setas e pontos do carrossel de upsell do carrinho.
 *
 * O arrastar com o dedo e o encaixe dos cards são do próprio navegador
 * (overflow-x + scroll-snap no CSS) — este arquivo só cuida do que o CSS não
 * faz: mover o scroll pelas setas, desenhar os pontos e manter os dois em
 * sincronia com a posição atual.
 *
 * Por que custom element e não um script solto: o drawer reescreve o próprio
 * innerHTML a cada mudança do carrinho (assets/cart.js, getSectionsToRender).
 * Um listener preso a um nó morreria no primeiro "adicionar"; já o
 * connectedCallback roda de novo sozinho quando o elemento novo entra no DOM.
 * O arquivo é carregado no topo de snippets/cart-drawer.liquid, fora da região
 * reescrita — script reinserido por innerHTML não executa.
 */
if (!customElements.get('m-cart-upsell')) {
  class MCartUpsell extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-upsell-track]');
      if (!this.track) return;

      this.dotsWrap = this.querySelector('[data-upsell-dots]');
      this.nav = this.querySelector('[data-upsell-nav]');
      this.prevBtn = this.querySelector('[data-upsell-prev]');
      this.nextBtn = this.querySelector('[data-upsell-next]');
      this.items = Array.from(this.track.children);

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

      // O drawer é display:none enquanto fechado, então medir agora daria tudo
      // zerado. O ResizeObserver remede quando ele ganha caixa, na abertura.
      if (typeof ResizeObserver === 'function') {
        this.observer = new ResizeObserver(this.onResize);
        this.observer.observe(this.track);
      } else {
        // Sem ResizeObserver, a abertura do drawer não avisa ninguém. O resize
        // da janela é o gatilho mais próximo que sobra.
        window.addEventListener('resize', this.onResize);
        this.usingWindowResize = true;
      }

      this.update();
    }

    disconnectedCallback() {
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      if (this.observer) this.observer.disconnect();
      if (this.usingWindowResize) window.removeEventListener('resize', this.onResize);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    /** Largura de um card mais o espaço entre eles — o passo de cada seta. */
    get stepSize() {
      if (!this.items.length) return 0;
      const rect = this.items[0].getBoundingClientRect();
      const style = getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
      return rect.width + gap;
    }

    get maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    /**
     * Quantas posições de parada existem de fato.
     *
     * Os pontos não podem ser um por card: com dois cards visíveis de cinco, o
     * scroll acaba antes do último card virar o primeiro, e os pontos finais
     * nunca acendiam nem levavam a lugar nenhum ao serem clicados.
     */
    get stopCount() {
      const step = this.stepSize;
      if (!step) return 1;
      // ceil, e não round: uma sobra de meio card ainda é uma parada a mais.
      // Com 2 cards de 250 num trilho de 428, sobram 80px — que precisam de um
      // segundo ponto para o cliente saber que há mais para ver.
      return Math.ceil(this.maxScroll / step) + 1;
    }

    /**
     * true quando não há rolagem que valha controles. O limiar é pequeno de
     * propósito: qualquer sobra real de conteúdo merece setas e pontos, e o
     * valor só existe para não ligar o carrossel por arredondamento de sub-pixel.
     */
    get isStatic() {
      return this.maxScroll < 24;
    }

    get currentIndex() {
      const step = this.stepSize;
      if (!step) return 0;
      // No fim do curso é sempre o último ponto, mesmo que o trecho final seja
      // menor que um passo — senão a última bolinha nunca acende.
      if (this.track.scrollLeft >= this.maxScroll - 2) return this.stopCount - 1;
      return Math.min(Math.round(this.track.scrollLeft / step), this.stopCount - 1);
    }

    /** Respeita quem pediu menos animação no sistema. */
    get scrollBehavior() {
      const mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
      return mq && mq.matches ? 'auto' : 'smooth';
    }

    step(direction) {
      const size = this.stepSize;
      if (!size) return;
      const alvo = Math.min(this.maxScroll, Math.max(0, this.track.scrollLeft + size * direction));
      this.track.scrollTo({ left: alvo, behavior: this.scrollBehavior });
    }

    buildDots(total) {
      if (!this.dotsWrap) return;
      this.dotsWrap.textContent = '';

      for (let i = 0; i < total; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'm-cart-upsell__dot';
        dot.setAttribute('aria-label', `${i + 1} de ${total}`);
        dot.addEventListener('click', () => {
          // Clampar aqui também: o último ponto aponta para o fim do scroll,
          // que é menor que índice × passo.
          const alvo = Math.min(this.stepSize * i, this.maxScroll);
          this.track.scrollTo({ left: alvo, behavior: this.scrollBehavior });
        });
        this.dotsWrap.appendChild(dot);
      }

      this.dots = Array.from(this.dotsWrap.children);
      this.dotsCount = total;
    }

    onScroll() {
      // O scroll dispara muitas vezes por gesto; um frame por vez basta para
      // manter pontos e setas em dia sem repintar à toa.
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    onResize() {
      this.update();
    }

    update() {
      const estatico = this.isStatic;

      if (this.nav) this.nav.hidden = estatico;
      if (this.dotsWrap) this.dotsWrap.hidden = estatico;
      if (estatico) return;

      // A largura do drawer muda entre celular e desktop, e com ela o número de
      // paradas — por isso os pontos são refeitos quando a conta muda.
      const total = this.stopCount;
      if (this.dotsCount !== total) this.buildDots(total);

      const atual = this.currentIndex;
      if (this.dots) {
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === atual);
          dot.setAttribute('aria-current', i === atual ? 'true' : 'false');
        });
      }

      // Desabilitar nas pontas em vez de esconder: a seta sumindo faria a
      // largura do cabeçalho pular no meio do gesto.
      if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 2;
      if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= this.maxScroll - 2;
    }
  }

  customElements.define('m-cart-upsell', MCartUpsell);
}
